import type {PropertyDefinition} from '@github-ui/custom-properties-types'

export interface DefinitionIceBreakerProps {
  description: string
  suggestedDefinition: Omit<PropertyDefinition, 'source'>
}

export const suggestions: DefinitionIceBreakerProps[] = [
  {
    description: 'Is this a production repository?',
    suggestedDefinition: {
      propertyName: 'deploys_to_production',
      valueType: 'true_false',
      required: true,
      defaultValue: 'false',
      description:
        'Indicates whether the indicated repository deploys to a production environment, or rather a staging or sandbox environment, for example.',
      regex: null,
      allowedValues: null,
      valuesEditableBy: 'org_actors',
      requireExplicitValues: false,
    },
  },
  {
    description: 'Who owns this repository?',
    suggestedDefinition: {
      propertyName: 'owner',
      valueType: 'string',
      required: false,
      defaultValue: null,
      description: 'The team that owns this repository.',
      regex: null,
      allowedValues: null,
      valuesEditableBy: 'org_actors',
      requireExplicitValues: false,
    },
  },
  {
    description: 'What compliance standards apply?',
    suggestedDefinition: {
      propertyName: 'compliance_tier',
      valueType: 'string',
      required: false,
      defaultValue: null,
      description:
        'The compliance standards that apply to this repository, which influence how its data must be handled.',
      regex: null,
      allowedValues: null,
      valuesEditableBy: 'org_actors',
      requireExplicitValues: false,
    },
  },
  {
    description: 'What lifecycle stage is this in?',
    suggestedDefinition: {
      propertyName: 'lifecycle',
      valueType: 'single_select',
      required: false,
      defaultValue: null,
      description: 'Describes the current lifecycle stage of the repository.',
      regex: null,
      allowedValues: ['experimental', 'development', 'production', 'maintained', 'deprecated'],
      valuesEditableBy: 'org_actors',
      requireExplicitValues: false,
    },
  },
  {
    description: 'How sensitive is this data?',
    suggestedDefinition: {
      propertyName: 'data_classification',
      valueType: 'single_select',
      required: false,
      defaultValue: null,
      description: 'Classifies the sensitivity of the data or source code associated with the repository.',
      regex: null,
      allowedValues: ['public', 'internal', 'confidential', 'restricted'],
      valuesEditableBy: 'org_actors',
      requireExplicitValues: false,
    },
  },
  {
    description: "What's the primary language?",
    suggestedDefinition: {
      propertyName: 'language',
      valueType: 'single_select',
      required: false,
      defaultValue: null,
      description: 'Indicates the primary programming language used in the repository.',
      regex: null,
      allowedValues: ['Java', 'Python', 'JavaScript', 'TypeScript', 'Go', 'Rust', 'C#', 'Ruby'],
      valuesEditableBy: 'org_actors',
      requireExplicitValues: false,
    },
  },
  {
    description: 'How business-critical is this?',
    suggestedDefinition: {
      propertyName: 'business_criticality',
      valueType: 'single_select',
      required: false,
      defaultValue: null,
      description: "Rates the repository's importance to business operations.",
      regex: null,
      allowedValues: ['low', 'medium', 'high', 'critical'],
      valuesEditableBy: 'org_actors',
      requireExplicitValues: false,
    },
  },
  {
    description: 'Does this handle personal data?',
    suggestedDefinition: {
      propertyName: 'pii',
      valueType: 'true_false',
      required: false,
      defaultValue: null,
      description: 'Indicates whether the repository may contain or process personally identifiable information.',
      regex: null,
      allowedValues: null,
      valuesEditableBy: 'org_actors',
      requireExplicitValues: false,
    },
  },
  {
    description: 'Is this internet-facing?',
    suggestedDefinition: {
      propertyName: 'internet_facing',
      valueType: 'true_false',
      required: false,
      defaultValue: null,
      description: 'Indicates whether software from this repository is accessible from the public internet.',
      regex: null,
      allowedValues: null,
      valuesEditableBy: 'org_actors',
      requireExplicitValues: false,
    },
  },
  {
    description: 'Which environment does this run in?',
    suggestedDefinition: {
      propertyName: 'environment',
      valueType: 'single_select',
      required: false,
      defaultValue: null,
      description: 'Indicates the primary deployment environment associated with the repository.',
      regex: null,
      allowedValues: ['development', 'staging', 'production'],
      valuesEditableBy: 'org_actors',
      requireExplicitValues: false,
    },
  },
  {
    description: 'What CI system does this use?',
    suggestedDefinition: {
      propertyName: 'ci',
      valueType: 'single_select',
      required: false,
      defaultValue: null,
      description: 'Specifies the continuous integration system used by the repository.',
      regex: null,
      allowedValues: ['github_actions', 'jenkins', 'circleci', 'gitlab_ci', 'azure_devops'],
      valuesEditableBy: 'org_actors',
      requireExplicitValues: false,
    },
  },
  {
    description: 'Which cloud provider hosts this?',
    suggestedDefinition: {
      propertyName: 'cloud',
      valueType: 'single_select',
      required: false,
      defaultValue: null,
      description: 'Identifies the primary cloud provider used by the repository.',
      regex: null,
      allowedValues: ['aws', 'azure', 'gcp', 'multi_cloud', 'on_premise'],
      valuesEditableBy: 'org_actors',
      requireExplicitValues: false,
    },
  },
  {
    description: 'Is this a monorepo?',
    suggestedDefinition: {
      propertyName: 'monorepo',
      valueType: 'true_false',
      required: false,
      defaultValue: null,
      description: 'Indicates whether the repository contains multiple distinct services, packages, or projects.',
      regex: null,
      allowedValues: null,
      valuesEditableBy: 'org_actors',
      requireExplicitValues: false,
    },
  },
  {
    description: 'Has this been archived?',
    suggestedDefinition: {
      propertyName: 'archived',
      valueType: 'true_false',
      required: false,
      defaultValue: null,
      description: 'Indicates whether the repository has been archived and is now read-only.',
      regex: null,
      allowedValues: null,
      valuesEditableBy: 'org_actors',
      requireExplicitValues: false,
    },
  },
  {
    description: 'Which license applies?',
    suggestedDefinition: {
      propertyName: 'license',
      valueType: 'single_select',
      required: false,
      defaultValue: null,
      description: 'Specifies the software license governing the repository.',
      regex: null,
      allowedValues: ['MIT', 'Apache-2.0', 'GPL-3.0', 'BSD-3-Clause', 'proprietary', 'unlicensed'],
      valuesEditableBy: 'org_actors',
      requireExplicitValues: false,
    },
  },
  {
    description: 'Which cost center owns this?',
    suggestedDefinition: {
      propertyName: 'cost_center',
      valueType: 'string',
      required: false,
      defaultValue: null,
      description:
        'Identifies the cost center or budget code associated with the repository for chargeback or financial reporting.',
      regex: null,
      allowedValues: null,
      valuesEditableBy: 'org_actors',
      requireExplicitValues: false,
    },
  },
  {
    description: 'Which Jira project tracks this?',
    suggestedDefinition: {
      propertyName: 'jira_project',
      valueType: 'string',
      required: false,
      defaultValue: null,
      description: 'The Jira project key associated with this repository.',
      regex: null,
      allowedValues: null,
      valuesEditableBy: 'org_actors',
      requireExplicitValues: false,
    },
  },
  {
    description: 'Which framework powers this?',
    suggestedDefinition: {
      propertyName: 'framework',
      valueType: 'single_select',
      required: false,
      defaultValue: null,
      description: 'Identifies the primary framework used to build the software in this repository.',
      regex: null,
      allowedValues: [
        '.NET',
        'Django',
        'Express',
        'Flask',
        'Laravel',
        'Next.js',
        'React',
        'Spring',
        'Vue',
        'Terraform',
        'Flutter',
      ],
      valuesEditableBy: 'org_actors',
      requireExplicitValues: false,
    },
  },
  {
    description: 'Which compliance frameworks apply?',
    suggestedDefinition: {
      propertyName: 'compliance',
      valueType: 'multi_select',
      required: false,
      defaultValue: null,
      description: 'Lists compliance frameworks or regulatory obligations relevant to the repository.',
      regex: null,
      allowedValues: ['ISO 27001', 'SOC 2', 'PCI-DSS', 'SOX', 'GDPR', 'HIPAA', 'FedRAMP'],
      valuesEditableBy: 'org_actors',
      requireExplicitValues: false,
    },
  },
  {
    description: 'Does this contain infrastructure as code?',
    suggestedDefinition: {
      propertyName: 'iac',
      valueType: 'true_false',
      required: false,
      defaultValue: null,
      description: 'Indicates whether the repository contains infrastructure-as-code.',
      regex: null,
      allowedValues: null,
      valuesEditableBy: 'org_actors',
      requireExplicitValues: false,
    },
  },
  {
    description: 'Who maintains this?',
    suggestedDefinition: {
      propertyName: 'maintainer',
      valueType: 'string',
      required: false,
      defaultValue: null,
      description: "Names the technical maintainer responsible for the repository's code quality and ongoing upkeep.",
      regex: null,
      allowedValues: null,
      valuesEditableBy: 'org_actors',
      requireExplicitValues: false,
    },
  },
]

export function getShuffledSuggestions(): DefinitionIceBreakerProps[] {
  const shuffled = [...suggestions]
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[shuffled[i], shuffled[j]] = [shuffled[j]!, shuffled[i]!]
  }
  return shuffled
}
